import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateLoyaltyEarningRulesResponse,
  CreateCustomLoyaltyEarningRuleOptions,
  CreateCustomLoyaltyEarningRuleResponse,
  DeleteLoyaltyEarningRuleOptions,
  ListEarningRulesOptions,
  ListEarningRulesResponse,
  LoyaltyEarningRule,
  LoyaltyEarningRuleCreatedEnvelope,
  LoyaltyEarningRuleDeletedEnvelope,
  LoyaltyEarningRuleUpdatedEnvelope,
  TypeWithLiterals,
  UpdateLoyaltyEarningRule,
  UpdateLoyaltyEarningRuleResponse,
  bulkCreateLoyaltyEarningRules as universalBulkCreateLoyaltyEarningRules,
  createCustomLoyaltyEarningRule as universalCreateCustomLoyaltyEarningRule,
  createLoyaltyEarningRule as universalCreateLoyaltyEarningRule,
  deleteAutomationEarningRule as universalDeleteAutomationEarningRule,
  deleteLoyaltyEarningRule as universalDeleteLoyaltyEarningRule,
  getLoyaltyEarningRule as universalGetLoyaltyEarningRule,
  listEarningRules as universalListEarningRules,
  updateLoyaltyEarningRule as universalUpdateLoyaltyEarningRule,
} from './loyalty-v1-loyalty-earning-rule-earning-rules.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/loyalty' };

export function createLoyaltyEarningRule(
  httpClient: HttpClient
): CreateLoyaltyEarningRuleSignature {
  return (
    earningRule: NonNullablePaths<
      LoyaltyEarningRule,
      | `sourceAppId`
      | `status`
      | `title`
      | `triggerActivityType`
      | `triggerAppId`,
      0
    >
  ) =>
    universalCreateLoyaltyEarningRule(
      earningRule,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateLoyaltyEarningRuleSignature {
  /**
   * Creates a non-automated earning rule.
   *
   * >**Note**: You can only create non-automated earning rules from a supported list.
   * For the supported list of services, see the Introduction.
   * @param - Earning rule to create.
   * @returns Created earning rule.
   */
  (
    earningRule: NonNullablePaths<
      LoyaltyEarningRule,
      | `sourceAppId`
      | `status`
      | `title`
      | `triggerActivityType`
      | `triggerAppId`,
      0
    >
  ): Promise<
    NonNullablePaths<
      LoyaltyEarningRule,
      | `fixedAmount.configs`
      | `fixedAmount.configs.${number}.points`
      | `conversionRate.configs`
      | `conversionRate.configs.${number}.moneyAmount`
      | `conversionRate.configs.${number}.points`
      | `sourceAppId`
      | `triggerAppId`
      | `triggerActivityType`
      | `title`
      | `status`
      | `metadata.canBeDeleted`
      | `triggerFilters`
      | `triggerFilters.${number}.fieldKey`,
      3
    >
  >;
}

export function bulkCreateLoyaltyEarningRules(
  httpClient: HttpClient
): BulkCreateLoyaltyEarningRulesSignature {
  return (
    earningRules: NonNullablePaths<
      LoyaltyEarningRule,
      | `sourceAppId`
      | `status`
      | `title`
      | `triggerActivityType`
      | `triggerAppId`,
      0
    >[]
  ) =>
    universalBulkCreateLoyaltyEarningRules(
      earningRules,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateLoyaltyEarningRulesSignature {
  /**
   * Creates multiple non-automated earning rules.
   *
   * >**Note**: You can only create non-automated earning rules from a supported list.
   * For the supported list of services, see the Introduction.
   * @param - Earning rules to create.
   */
  (
    earningRules: NonNullablePaths<
      LoyaltyEarningRule,
      | `sourceAppId`
      | `status`
      | `title`
      | `triggerActivityType`
      | `triggerAppId`,
      0
    >[]
  ): Promise<
    NonNullablePaths<
      BulkCreateLoyaltyEarningRulesResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.sourceAppId`
      | `results.${number}.item.triggerAppId`
      | `results.${number}.item.triggerActivityType`
      | `results.${number}.item.title`
      | `results.${number}.item.status`
      | `results.${number}.item.metadata.canBeDeleted`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      4
    >
  >;
}

export function createCustomLoyaltyEarningRule(
  httpClient: HttpClient
): CreateCustomLoyaltyEarningRuleSignature {
  return (
    type: TypeWithLiterals,
    options?: CreateCustomLoyaltyEarningRuleOptions
  ) =>
    universalCreateCustomLoyaltyEarningRule(
      type,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCustomLoyaltyEarningRuleSignature {
  /**
   * Creates a custom automated earning rule.
   *
   * To learn more about the automated rules, see the Introduction.
   * @param - Type of the custom earning rule.
   */
  (
    type: TypeWithLiterals,
    options?: CreateCustomLoyaltyEarningRuleOptions
  ): Promise<
    NonNullablePaths<
      CreateCustomLoyaltyEarningRuleResponse,
      | `earningRule.fixedAmount.configs`
      | `earningRule.fixedAmount.configs.${number}.points`
      | `earningRule.conversionRate.configs`
      | `earningRule.conversionRate.configs.${number}.moneyAmount`
      | `earningRule.conversionRate.configs.${number}.points`
      | `earningRule.sourceAppId`
      | `earningRule.triggerAppId`
      | `earningRule.triggerActivityType`
      | `earningRule.title`
      | `earningRule.status`
      | `earningRule.metadata.canBeDeleted`
      | `earningRule.triggerFilters`
      | `earningRule.triggerFilters.${number}.fieldKey`,
      4
    >
  >;
}

export function getLoyaltyEarningRule(
  httpClient: HttpClient
): GetLoyaltyEarningRuleSignature {
  return (_id: string) =>
    universalGetLoyaltyEarningRule(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetLoyaltyEarningRuleSignature {
  /**
   * Retrieves a specified non-automated earning rule.
   *
   * To retrieve both automated and non-automated earning rules, call List Earning Rules.
   * @param - ID of the earning rule to retrieve.
   * @returns Retrieved earning rule.
   */
  (_id: string): Promise<
    NonNullablePaths<
      LoyaltyEarningRule,
      | `fixedAmount.configs`
      | `fixedAmount.configs.${number}.points`
      | `conversionRate.configs`
      | `conversionRate.configs.${number}.moneyAmount`
      | `conversionRate.configs.${number}.points`
      | `sourceAppId`
      | `triggerAppId`
      | `triggerActivityType`
      | `title`
      | `status`
      | `metadata.canBeDeleted`
      | `triggerFilters`
      | `triggerFilters.${number}.fieldKey`,
      3
    >
  >;
}

export function updateLoyaltyEarningRule(
  httpClient: HttpClient
): UpdateLoyaltyEarningRuleSignature {
  return (_id: string, earningRule: UpdateLoyaltyEarningRule) =>
    universalUpdateLoyaltyEarningRule(
      _id,
      earningRule,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateLoyaltyEarningRuleSignature {
  /**
   * Updates an earning rule.
   *
   * Supports partial updates.
   *
   * Revision number, which increments by 1 each time the earning rule is updated. To prevent conflicting changes,
   * the current `revision` must be passed when updating the earning rule.
   * @param - Loyalty earning rule ID.
   */
  (_id: string, earningRule: UpdateLoyaltyEarningRule): Promise<
    NonNullablePaths<
      UpdateLoyaltyEarningRuleResponse,
      | `earningRule.fixedAmount.configs`
      | `earningRule.fixedAmount.configs.${number}.points`
      | `earningRule.conversionRate.configs`
      | `earningRule.conversionRate.configs.${number}.moneyAmount`
      | `earningRule.conversionRate.configs.${number}.points`
      | `earningRule.sourceAppId`
      | `earningRule.triggerAppId`
      | `earningRule.triggerActivityType`
      | `earningRule.title`
      | `earningRule.status`
      | `earningRule.metadata.canBeDeleted`
      | `earningRule.triggerFilters`
      | `earningRule.triggerFilters.${number}.fieldKey`,
      4
    >
  >;
}

export function deleteLoyaltyEarningRule(
  httpClient: HttpClient
): DeleteLoyaltyEarningRuleSignature {
  return (_id: string, options?: DeleteLoyaltyEarningRuleOptions) =>
    universalDeleteLoyaltyEarningRule(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteLoyaltyEarningRuleSignature {
  /**
   * Deletes a non-automated earning rule.
   *
   * To delete an automated earning rule, call
   * Delete Automation Earning Rule.
   *
   * To update an earning rule's status instead of deleting it, call
   * Update Loyalty Earning Rule.
   * @param - ID of the earning rule to delete.
   */
  (_id: string, options?: DeleteLoyaltyEarningRuleOptions): Promise<void>;
}

export function deleteAutomationEarningRule(
  httpClient: HttpClient
): DeleteAutomationEarningRuleSignature {
  return (_id: string) =>
    universalDeleteAutomationEarningRule(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteAutomationEarningRuleSignature {
  /**
   * Deletes a custom automated earning rule.
   * Pre-installed automated rules can only be paused, not deleted.
   *
   * To update an earning rule's status instead of deleting it, call
   * Update Loyalty Earning Rule.
   *
   * To delete a non-automated earning rule, call
   * Delete Loyalty Earning Rule.
   * @param - ID of the earning rule to delete.
   */
  (_id: string): Promise<void>;
}

export function listEarningRules(
  httpClient: HttpClient
): ListEarningRulesSignature {
  return (options?: ListEarningRulesOptions) =>
    universalListEarningRules(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListEarningRulesSignature {
  /**
   * Retrieves a list of earning rules.
   *
   * Returns both automated and non-automated earning rules.
   *
   * You can filter the results by `triggerAppId` or `triggerActivityType`.
   */
  (options?: ListEarningRulesOptions): Promise<
    NonNullablePaths<
      ListEarningRulesResponse,
      | `earningRules`
      | `earningRules.${number}.sourceAppId`
      | `earningRules.${number}.triggerAppId`
      | `earningRules.${number}.triggerActivityType`
      | `earningRules.${number}.title`
      | `earningRules.${number}.status`
      | `earningRules.${number}.metadata.canBeDeleted`,
      3
    >
  >;
}

export const onLoyaltyEarningRuleCreated = /*#__PURE__*/ EventDefinition(
  'wix.loyalty.v1.loyalty_earning_rule_created',
  true,
  (event: LoyaltyEarningRuleCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<LoyaltyEarningRuleCreatedEnvelope>();
export const onLoyaltyEarningRuleDeleted = /*#__PURE__*/ EventDefinition(
  'wix.loyalty.v1.loyalty_earning_rule_deleted',
  true,
  (event: LoyaltyEarningRuleDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<LoyaltyEarningRuleDeletedEnvelope>();
export const onLoyaltyEarningRuleUpdated = /*#__PURE__*/ EventDefinition(
  'wix.loyalty.v1.loyalty_earning_rule_updated',
  true,
  (event: LoyaltyEarningRuleUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<LoyaltyEarningRuleUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AppInstallationInLoyaltySite,
  ApplicationError,
  AutomationTriggerFilter,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateLoyaltyEarningRulesRequest,
  BulkCreateLoyaltyEarningRulesResponse,
  BulkLoyaltyEarningRuleResult,
  CacheInvalidated,
  ConversionRate,
  ConversionRateConfig,
  CreateCustomLoyaltyEarningRuleOptions,
  CreateCustomLoyaltyEarningRuleRequest,
  CreateCustomLoyaltyEarningRuleResponse,
  CreateLoyaltyEarningRuleRequest,
  CreateLoyaltyEarningRuleResponse,
  CursorPaging,
  Cursors,
  CustomLoyaltyEarningRule,
  CustomLoyaltyEarningRuleTypeOneOf,
  DeleteAutomationEarningRuleRequest,
  DeleteAutomationEarningRuleResponse,
  DeleteLoyaltyEarningRuleOptions,
  DeleteLoyaltyEarningRuleRequest,
  DeleteLoyaltyEarningRuleResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EarningRuleDisabled,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  FixedAmount,
  FixedAmountConfig,
  GetLoyaltyEarningRuleRequest,
  GetLoyaltyEarningRuleResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  InvalidateEarningRuleCacheRequest,
  InvalidateEarningRuleCacheResponse,
  ItemMetadata,
  ListEarningRulesInTierRequest,
  ListEarningRulesInTierResponse,
  ListEarningRulesOptions,
  ListEarningRulesRequest,
  ListEarningRulesResponse,
  LoyaltyEarningRule,
  LoyaltyEarningRuleCreatedEnvelope,
  LoyaltyEarningRuleDeletedEnvelope,
  LoyaltyEarningRuleTypeOneOf,
  LoyaltyEarningRuleTypeTag,
  LoyaltyEarningRuleTypeTagType,
  LoyaltyEarningRuleUpdatedEnvelope,
  MessageEnvelope,
  Metadata,
  PagingMetadataV2,
  RestoreInfo,
  Status,
  Type,
  UpdateLoyaltyEarningRule,
  UpdateLoyaltyEarningRuleRequest,
  UpdateLoyaltyEarningRuleResponse,
  V1Type,
  WebhookIdentityType,
} from './loyalty-v1-loyalty-earning-rule-earning-rules.universal.js';
